<!-- AlloyMonitoringModulesSection.vue -->
<template>
  <!-- Serverspace Monitoring Modules -->
  <div v-if="isLinux || isWindows" class="space-y-6 glass-panel dark:text-bg-gray">
    <div class="flex items-center gap-2 mb-4">
      <h2 class="font-semibold text-gray-800 dark:text-gray-200 capitalize">
        Monitoring Modules <span class="text-gray-400 dark:text-gray-500 normal-case font-normal">(Optional)</span>
      </h2>
      <div class="relative group">
        <button type="button" class="bg-transparent hover:bg-transparent" @click="$emit('start-tutorial')" aria-label="Section tutorial">
          <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="dark:text-gray-50 text-gray-900"><circle cx="12" cy="12" r="10"/><path d="M9.09 9a3 3 0 0 1 5.83 1c0 2-3 3-3 3"/><path d="M12 17h.01"/></svg>
        </button>
        <span class="absolute top-full text-xs left-0 glass-card mb-1 px-1.5 py-0.5 text-white bg-gray-800 dark:bg-gray-700 rounded opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none whitespace-nowrap">Tutorial</span>
      </div>
    </div>

    <div class="columns-1 lg:columns-2 gap-6">
    <!-- eBPF (Beyla) zero-touch app monitoring (Linux) -->
    <div v-if="isLinux" class="mb-6 break-inside-avoid p-4 rounded-lg border border-gray-300 dark:border-gray-500 shadow-sm">
      <div class="flex items-center justify-between">
        <div>
          <p class="font-medium text-gray-700 dark:text-gray-50">eBPF App Monitoring (Beyla)</p>
          <p class="text-xs text-gray-500 dark:text-gray-400">Zero-touch traces + span metrics, no app restart or SDK.</p>
        </div>
        <div class="relative inline-block w-12 align-middle select-none">
          <input v-model="modelValue.ebpf_enabled" id="ebpf_enabled" type="checkbox"
            class="absolute block w-6 h-6 rounded-full bg-white border-4 appearance-none cursor-pointer peer checked:right-0 checked:bg-blue-600 transition-all duration-300" />
          <label for="ebpf_enabled" class="block overflow-hidden h-6 rounded-full bg-gray-300 peer-checked:bg-blue-500 cursor-pointer"></label>
        </div>
      </div>
      <div v-if="modelValue.ebpf_enabled" class="grid grid-cols-2 gap-4 mt-4">
        <div class="flex flex-col">
          <label for="ebpf_app_name" class="mb-1 font-medium text-gray-700 dark:text-gray-50">Application Name</label>
          <input id="ebpf_app_name" v-model="modelValue.ebpf_app_name" placeholder="app"
            class="border border-gray-300 dark:bg-gray-600 dark:text-gray-50 rounded px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500" />
        </div>
        <div class="flex flex-col">
          <label for="ebpf_open_ports" class="mb-1 font-medium text-gray-700 dark:text-gray-50">Open Ports</label>
          <input id="ebpf_open_ports" v-model="modelValue.ebpf_open_ports" placeholder="8080,8443"
            class="border border-gray-300 dark:bg-gray-600 dark:text-gray-50 rounded px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500" />
        </div>
        <div class="flex flex-col md:col-span-2">
          <label class="mb-1 font-medium text-gray-700 dark:text-gray-50">Instrumentations</label>
          <div class="flex flex-wrap gap-2">
            <button
              v-for="option in instrumentationOptions"
              :key="option"
              type="button"
              @click="toggleInstrumentation(option)"
              :aria-pressed="isInstrumentationSelected(option)"
              class="px-3 py-1.5 rounded-md text-xs font-medium border transition-colors"
              :class="isInstrumentationSelected(option)
                ? 'bg-green-400 text-gray-900 border-green-400'
                : 'bg-gray-700 text-gray-200 border-gray-600 hover:bg-gray-600'">
              {{ option }}
            </button>
          </div>
        </div>
        <div class="flex flex-col">
          <label for="ebpf_routes_unmatched" class="mb-1 font-medium text-gray-700 dark:text-gray-50">Unmatched Routes</label>
          <GlassSelect
            id="ebpf_routes_unmatched"
            v-model="modelValue.ebpf_routes_unmatched"
            :options="routesUnmatchedOptions"
            valueKey="value"
            labelKey="label"
            placeholder="Select mode"
          />
        </div>
        <div class="flex flex-col">
          <label for="ebpf_log_path" class="mb-1 font-medium text-gray-700 dark:text-gray-50">Access Log Path (Optional)</label>
          <input id="ebpf_log_path" v-model="modelValue.ebpf_log_path" placeholder="/var/log/app/access.log"
            class="border border-gray-300 dark:bg-gray-600 dark:text-gray-50 rounded px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500" />
        </div>
        <div class="flex flex-col">
          <label for="ebpf_service_graph" class="mb-1 font-medium text-gray-700 dark:text-gray-50">Service Graph Metrics</label>
          <div class="relative inline-block w-12 align-middle select-none">
            <input v-model="modelValue.ebpf_service_graph" id="ebpf_service_graph" type="checkbox"
              class="absolute block w-6 h-6 rounded-full bg-white border-4 appearance-none cursor-pointer peer checked:right-0 checked:bg-blue-600 transition-all duration-300" />
            <label for="ebpf_service_graph" class="block overflow-hidden h-6 rounded-full bg-gray-300 peer-checked:bg-blue-500 cursor-pointer"></label>
          </div>
        </div>
      </div>
    </div>

    <!-- Linux base collectors -->
    <template v-if="isLinux">
      <div v-for="mod in linuxCollectorModules" :key="mod.field"
        class="mb-6 break-inside-avoid p-4 rounded-lg border border-gray-300 dark:border-gray-500 shadow-sm">
        <div class="flex items-center justify-between">
          <div>
            <p class="font-medium text-gray-700 dark:text-gray-50">{{ mod.label }}</p>
            <p class="text-xs text-gray-500 dark:text-gray-400">{{ mod.description }}</p>
          </div>
          <div class="relative inline-block w-12 align-middle select-none">
            <input v-model="modelValue[mod.field]" :id="mod.field" type="checkbox"
              class="absolute block w-6 h-6 rounded-full bg-white border-4 appearance-none cursor-pointer peer checked:right-0 checked:bg-blue-600 transition-all duration-300" />
            <label :for="mod.field" class="block overflow-hidden h-6 rounded-full bg-gray-300 peer-checked:bg-blue-500 cursor-pointer"></label>
          </div>
        </div>
      </div>
    </template>

    <!-- IIS (Windows) -->
    <div v-if="isWindows" class="mb-6 break-inside-avoid p-4 rounded-lg border border-gray-300 dark:border-gray-500 shadow-sm">
      <div class="flex items-center justify-between">
        <div>
          <p class="font-medium text-gray-700 dark:text-gray-50">IIS</p>
          <p class="text-xs text-gray-500 dark:text-gray-400">Dedicated IIS metrics collector + W3C access-log pipeline.</p>
        </div>
        <div class="relative inline-block w-12 align-middle select-none">
          <input v-model="modelValue.iis_enabled" id="iis_enabled" type="checkbox"
            class="absolute block w-6 h-6 rounded-full bg-white border-4 appearance-none cursor-pointer peer checked:right-0 checked:bg-blue-600 transition-all duration-300" />
          <label for="iis_enabled" class="block overflow-hidden h-6 rounded-full bg-gray-300 peer-checked:bg-blue-500 cursor-pointer"></label>
        </div>
      </div>
      <div v-if="modelValue.iis_enabled" class="grid grid-cols-2 gap-4 mt-4">
        <div class="flex flex-col">
          <label for="iis_site_include" class="mb-1 font-medium text-gray-700 dark:text-gray-50">Site Include (regex)</label>
          <input id="iis_site_include" v-model="modelValue.iis_site_include" placeholder=".*"
            class="border border-gray-300 dark:bg-gray-600 dark:text-gray-50 rounded px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500" />
        </div>
        <div class="flex flex-col">
          <label for="iis_site_exclude" class="mb-1 font-medium text-gray-700 dark:text-gray-50">Site Exclude (regex)</label>
          <input id="iis_site_exclude" v-model="modelValue.iis_site_exclude" placeholder=""
            class="border border-gray-300 dark:bg-gray-600 dark:text-gray-50 rounded px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500" />
        </div>
        <div class="flex flex-col">
          <label for="iis_app_include" class="mb-1 font-medium text-gray-700 dark:text-gray-50">App Include (regex)</label>
          <input id="iis_app_include" v-model="modelValue.iis_app_include" placeholder=".*"
            class="border border-gray-300 dark:bg-gray-600 dark:text-gray-50 rounded px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500" />
        </div>
        <div class="flex flex-col">
          <label for="iis_app_exclude" class="mb-1 font-medium text-gray-700 dark:text-gray-50">App Exclude (regex)</label>
          <input id="iis_app_exclude" v-model="modelValue.iis_app_exclude" placeholder=""
            class="border border-gray-300 dark:bg-gray-600 dark:text-gray-50 rounded px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500" />
        </div>
        <div class="flex flex-col">
          <label for="iis_log_enabled" class="mb-1 font-medium text-gray-700 dark:text-gray-50">Access Log Pipeline</label>
          <div class="relative inline-block w-12 align-middle select-none">
            <input v-model="modelValue.iis_log_enabled" id="iis_log_enabled" type="checkbox"
              class="absolute block w-6 h-6 rounded-full bg-white border-4 appearance-none cursor-pointer peer checked:right-0 checked:bg-blue-600 transition-all duration-300" />
            <label for="iis_log_enabled" class="block overflow-hidden h-6 rounded-full bg-gray-300 peer-checked:bg-blue-500 cursor-pointer"></label>
          </div>
        </div>
        <div class="flex flex-col">
          <label for="iis_log_metrics_enabled" class="mb-1 font-medium text-gray-700 dark:text-gray-50">Access-Log Derived Metrics</label>
          <div class="relative inline-block w-12 align-middle select-none">
            <input v-model="modelValue.iis_log_metrics_enabled" id="iis_log_metrics_enabled" type="checkbox"
              class="absolute block w-6 h-6 rounded-full bg-white border-4 appearance-none cursor-pointer peer checked:right-0 checked:bg-blue-600 transition-all duration-300" />
            <label for="iis_log_metrics_enabled" class="block overflow-hidden h-6 rounded-full bg-gray-300 peer-checked:bg-blue-500 cursor-pointer"></label>
          </div>
        </div>
        <div v-if="modelValue.iis_log_enabled" class="flex flex-col md:col-span-2">
          <label for="iis_log_path" class="mb-1 font-medium text-gray-700 dark:text-gray-50">Access Log Path</label>
          <input id="iis_log_path" v-model="modelValue.iis_log_path" placeholder="C:/inetpub/logs/LogFiles/W3SVC*/u_ex*.log"
            class="border border-gray-300 dark:bg-gray-600 dark:text-gray-50 rounded px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500" />
        </div>
      </div>
    </div>

    <!-- Hyper-V (Windows) -->
    <div v-if="isWindows" class="mb-6 break-inside-avoid p-4 rounded-lg border border-gray-300 dark:border-gray-500 shadow-sm">
      <div class="flex items-center justify-between">
        <div>
          <p class="font-medium text-gray-700 dark:text-gray-50">Hyper-V</p>
          <p class="text-xs text-gray-500 dark:text-gray-400">Metrics only, requires the Hyper-V role on the host.</p>
        </div>
        <div class="relative inline-block w-12 align-middle select-none">
          <input v-model="modelValue.hyperv_enabled" id="hyperv_enabled" type="checkbox"
            class="absolute block w-6 h-6 rounded-full bg-white border-4 appearance-none cursor-pointer peer checked:right-0 checked:bg-blue-600 transition-all duration-300" />
          <label for="hyperv_enabled" class="block overflow-hidden h-6 rounded-full bg-gray-300 peer-checked:bg-blue-500 cursor-pointer"></label>
        </div>
      </div>
    </div>

    <!-- Active Directory (Windows) -->
    <div v-if="isWindows" class="mb-6 break-inside-avoid p-4 rounded-lg border border-gray-300 dark:border-gray-500 shadow-sm">
      <div class="flex items-center justify-between">
        <div>
          <p class="font-medium text-gray-700 dark:text-gray-50">Active Directory</p>
          <p class="text-xs text-gray-500 dark:text-gray-400">Metrics only, requires the host to be a domain controller.</p>
        </div>
        <div class="relative inline-block w-12 align-middle select-none">
          <input v-model="modelValue.ad_enabled" id="ad_enabled" type="checkbox"
            class="absolute block w-6 h-6 rounded-full bg-white border-4 appearance-none cursor-pointer peer checked:right-0 checked:bg-blue-600 transition-all duration-300" />
          <label for="ad_enabled" class="block overflow-hidden h-6 rounded-full bg-gray-300 peer-checked:bg-blue-500 cursor-pointer"></label>
        </div>
      </div>
    </div>

    <!-- Windows base collectors -->
    <template v-if="isWindows">
      <div v-for="mod in windowsCollectorModules" :key="mod.field"
        class="mb-6 break-inside-avoid p-4 rounded-lg border border-gray-300 dark:border-gray-500 shadow-sm">
        <div class="flex items-center justify-between">
          <div>
            <p class="font-medium text-gray-700 dark:text-gray-50">{{ mod.label }}</p>
            <p class="text-xs text-gray-500 dark:text-gray-400">{{ mod.description }}</p>
          </div>
          <div class="relative inline-block w-12 align-middle select-none">
            <input v-model="modelValue[mod.field]" :id="mod.field" type="checkbox"
              class="absolute block w-6 h-6 rounded-full bg-white border-4 appearance-none cursor-pointer peer checked:right-0 checked:bg-blue-600 transition-all duration-300" />
            <label :for="mod.field" class="block overflow-hidden h-6 rounded-full bg-gray-300 peer-checked:bg-blue-500 cursor-pointer"></label>
          </div>
        </div>
      </div>
    </template>

    <!-- Apache -->
    <div class="mb-6 break-inside-avoid p-4 rounded-lg border border-gray-300 dark:border-gray-500 shadow-sm">
      <div class="flex items-center justify-between">
        <div>
          <p class="font-medium text-gray-700 dark:text-gray-50">Apache</p>
          <p class="text-xs text-gray-500 dark:text-gray-400">Requires mod_status with ExtendedStatus On.</p>
        </div>
        <div class="relative inline-block w-12 align-middle select-none">
          <input v-model="modelValue.apache_enabled" id="apache_enabled" type="checkbox"
            class="absolute block w-6 h-6 rounded-full bg-white border-4 appearance-none cursor-pointer peer checked:right-0 checked:bg-blue-600 transition-all duration-300" />
          <label for="apache_enabled" class="block overflow-hidden h-6 rounded-full bg-gray-300 peer-checked:bg-blue-500 cursor-pointer"></label>
        </div>
      </div>
      <div v-if="modelValue.apache_enabled" class="grid grid-cols-2 gap-4 mt-4">
        <div class="flex flex-col">
          <label for="apache_status_port" class="mb-1 font-medium text-gray-700 dark:text-gray-50">Status Port</label>
          <input id="apache_status_port" v-model.number="modelValue.apache_status_port" type="number" min="1"
            @focus="$emit('clear-error', 'apache_status_port')"
            :class="[
              'border rounded px-3 py-2 focus:outline-none dark:bg-gray-600 dark:text-gray-50',
              errors.apache_status_port
                ? 'border-red-500 ring-2 ring-red-500 focus:ring-red-500'
                : 'border-gray-300 focus:ring-2 focus:ring-blue-500'
            ]" />
          <FieldError :message="errors.apache_status_port" />
        </div>
        <div class="flex flex-col">
          <label for="apache_log_enabled" class="mb-1 font-medium text-gray-700 dark:text-gray-50">Access Log Pipeline</label>
          <div class="relative inline-block w-12 align-middle select-none">
            <input v-model="modelValue.apache_log_enabled" id="apache_log_enabled" type="checkbox"
              class="absolute block w-6 h-6 rounded-full bg-white border-4 appearance-none cursor-pointer peer checked:right-0 checked:bg-blue-600 transition-all duration-300" />
            <label for="apache_log_enabled" class="block overflow-hidden h-6 rounded-full bg-gray-300 peer-checked:bg-blue-500 cursor-pointer"></label>
          </div>
        </div>
        <div v-if="modelValue.apache_log_enabled" class="flex flex-col md:col-span-2">
          <label for="apache_log_path" class="mb-1 font-medium text-gray-700 dark:text-gray-50">Access Log Path</label>
          <input id="apache_log_path" v-model="modelValue.apache_log_path" placeholder="/var/log/apache2/access.log"
            class="border border-gray-300 dark:bg-gray-600 dark:text-gray-50 rounded px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500" />
        </div>
      </div>
    </div>

    <!-- MSSQL -->
    <div class="mb-6 break-inside-avoid p-4 rounded-lg border border-gray-300 dark:border-gray-500 shadow-sm">
      <div class="flex items-center justify-between">
        <div>
          <p class="font-medium text-gray-700 dark:text-gray-50">SQL Server (MSSQL)</p>
          <p class="text-xs text-gray-500 dark:text-gray-400">Connection string is stored with the config and rendered into the agent.</p>
        </div>
        <div class="relative inline-block w-12 align-middle select-none">
          <input v-model="modelValue.mssql_enabled" id="mssql_enabled" type="checkbox"
            class="absolute block w-6 h-6 rounded-full bg-white border-4 appearance-none cursor-pointer peer checked:right-0 checked:bg-blue-600 transition-all duration-300" />
          <label for="mssql_enabled" class="block overflow-hidden h-6 rounded-full bg-gray-300 peer-checked:bg-blue-500 cursor-pointer"></label>
        </div>
      </div>
      <div v-if="modelValue.mssql_enabled" class="grid grid-cols-2 gap-4 mt-4">
        <div class="flex flex-col md:col-span-2">
          <label for="mssql_connection_string" class="mb-1 font-medium text-gray-700 dark:text-gray-50">Connection String</label>
          <input id="mssql_connection_string" v-model="modelValue.mssql_connection_string" type="password" autocomplete="new-password"
            @focus="$emit('clear-error', 'mssql_connection_string')"
            :placeholder="modelValue.mssql_connection_string_set ? '•••••• stored — leave blank to keep current' : 'sqlserver://user:pass@host:1433'"
            :class="[
              'border rounded px-3 py-2 focus:outline-none dark:bg-gray-600 dark:text-gray-50',
              errors.mssql_connection_string
                ? 'border-red-500 ring-2 ring-red-500 focus:ring-red-500'
                : 'border-gray-300 focus:ring-2 focus:ring-blue-500'
            ]" />
          <FieldError :message="errors.mssql_connection_string" />
        </div>
        <div class="flex flex-col">
          <label for="mssql_max_idle_connections" class="mb-1 font-medium text-gray-700 dark:text-gray-50">Max Idle Connections</label>
          <input id="mssql_max_idle_connections" v-model.number="modelValue.mssql_max_idle_connections" type="number" min="0"
            class="border border-gray-300 dark:bg-gray-600 dark:text-gray-50 rounded px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500" />
        </div>
        <div class="flex flex-col">
          <label for="mssql_max_open_connections" class="mb-1 font-medium text-gray-700 dark:text-gray-50">Max Open Connections</label>
          <input id="mssql_max_open_connections" v-model.number="modelValue.mssql_max_open_connections" type="number" min="0"
            class="border border-gray-300 dark:bg-gray-600 dark:text-gray-50 rounded px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500" />
        </div>
        <div class="flex flex-col">
          <label for="mssql_timeout" class="mb-1 font-medium text-gray-700 dark:text-gray-50">Scrape Timeout</label>
          <input id="mssql_timeout" v-model="modelValue.mssql_timeout" placeholder="10s"
            class="border border-gray-300 dark:bg-gray-600 dark:text-gray-50 rounded px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500" />
        </div>
      </div>
    </div>

    <!-- Postgres -->
    <div class="mb-6 break-inside-avoid p-4 rounded-lg border border-gray-300 dark:border-gray-500 shadow-sm">
      <div class="flex items-center justify-between">
        <div>
          <p class="font-medium text-gray-700 dark:text-gray-50">PostgreSQL</p>
          <p class="text-xs text-gray-500 dark:text-gray-400">DSN is stored with the config; the login needs the pg_monitor role.</p>
        </div>
        <div class="relative inline-block w-12 align-middle select-none">
          <input v-model="modelValue.postgres_enabled" id="postgres_enabled" type="checkbox"
            class="absolute block w-6 h-6 rounded-full bg-white border-4 appearance-none cursor-pointer peer checked:right-0 checked:bg-blue-600 transition-all duration-300" />
          <label for="postgres_enabled" class="block overflow-hidden h-6 rounded-full bg-gray-300 peer-checked:bg-blue-500 cursor-pointer"></label>
        </div>
      </div>
      <div v-if="modelValue.postgres_enabled" class="grid grid-cols-2 gap-4 mt-4">
        <div class="flex flex-col md:col-span-2">
          <label for="postgres_dsn" class="mb-1 font-medium text-gray-700 dark:text-gray-50">Connection DSN</label>
          <input id="postgres_dsn" v-model="modelValue.postgres_dsn" type="password" autocomplete="new-password"
            @focus="$emit('clear-error', 'postgres_dsn')"
            :placeholder="modelValue.postgres_dsn_set ? '•••••• stored — leave blank to keep current' : 'postgresql://user:pass@host:5432/db?sslmode=require'"
            :class="[
              'border rounded px-3 py-2 focus:outline-none dark:bg-gray-600 dark:text-gray-50',
              errors.postgres_dsn
                ? 'border-red-500 ring-2 ring-red-500 focus:ring-red-500'
                : 'border-gray-300 focus:ring-2 focus:ring-blue-500'
            ]" />
          <FieldError :message="errors.postgres_dsn" />
        </div>
        <div class="flex flex-col">
          <label for="postgres_stat_statements" class="mb-1 font-medium text-gray-700 dark:text-gray-50">stat_statements Collector</label>
          <div class="relative inline-block w-12 align-middle select-none">
            <input v-model="modelValue.postgres_stat_statements" id="postgres_stat_statements" type="checkbox"
              class="absolute block w-6 h-6 rounded-full bg-white border-4 appearance-none cursor-pointer peer checked:right-0 checked:bg-blue-600 transition-all duration-300" />
            <label for="postgres_stat_statements" class="block overflow-hidden h-6 rounded-full bg-gray-300 peer-checked:bg-blue-500 cursor-pointer"></label>
          </div>
        </div>
        <div class="flex flex-col">
          <label for="postgres_stat_user_tables" class="mb-1 font-medium text-gray-700 dark:text-gray-50">stat_user_tables Collector</label>
          <div class="relative inline-block w-12 align-middle select-none">
            <input v-model="modelValue.postgres_stat_user_tables" id="postgres_stat_user_tables" type="checkbox"
              class="absolute block w-6 h-6 rounded-full bg-white border-4 appearance-none cursor-pointer peer checked:right-0 checked:bg-blue-600 transition-all duration-300" />
            <label for="postgres_stat_user_tables" class="block overflow-hidden h-6 rounded-full bg-gray-300 peer-checked:bg-blue-500 cursor-pointer"></label>
          </div>
        </div>
      </div>
    </div>

    <!-- VMware (staff only) -->
    <div v-if="isStaff && showVmware" class="mb-6 break-inside-avoid p-4 rounded-lg border border-gray-300 dark:border-gray-500 shadow-sm">
      <div class="flex items-center justify-between">
        <div>
          <p class="font-medium text-gray-700 dark:text-gray-50">VMware (vCenter / ESXi)</p>
          <p class="text-xs text-gray-500 dark:text-gray-400">Read-only vCenter/ESXi credentials stored with the config. vSphere 7.0 / 8.x.</p>
        </div>
        <div class="relative inline-block w-12 align-middle select-none">
          <input v-model="modelValue.vmware_enabled" id="vmware_enabled" type="checkbox"
            class="absolute block w-6 h-6 rounded-full bg-white border-4 appearance-none cursor-pointer peer checked:right-0 checked:bg-blue-600 transition-all duration-300" />
          <label for="vmware_enabled" class="block overflow-hidden h-6 rounded-full bg-gray-300 peer-checked:bg-blue-500 cursor-pointer"></label>
        </div>
      </div>
      <div v-if="modelValue.vmware_enabled" class="grid grid-cols-2 gap-4 mt-4">
        <div class="flex flex-col md:col-span-2">
          <label for="vmware_endpoint" class="mb-1 font-medium text-gray-700 dark:text-gray-50">Endpoint</label>
          <input id="vmware_endpoint" v-model="modelValue.vmware_endpoint" placeholder="https://vcenter.example.com"
            @focus="$emit('clear-error', 'vmware_endpoint')"
            :class="[
              'border rounded px-3 py-2 focus:outline-none dark:bg-gray-600 dark:text-gray-50',
              errors.vmware_endpoint
                ? 'border-red-500 ring-2 ring-red-500 focus:ring-red-500'
                : 'border-gray-300 focus:ring-2 focus:ring-blue-500'
            ]" />
          <FieldError :message="errors.vmware_endpoint" />
        </div>
        <div class="flex flex-col">
          <label for="vmware_username" class="mb-1 font-medium text-gray-700 dark:text-gray-50">Username</label>
          <input id="vmware_username" v-model="modelValue.vmware_username" placeholder="readonly@vsphere.local"
            @focus="$emit('clear-error', 'vmware_username')"
            :class="[
              'border rounded px-3 py-2 focus:outline-none dark:bg-gray-600 dark:text-gray-50',
              errors.vmware_username
                ? 'border-red-500 ring-2 ring-red-500 focus:ring-red-500'
                : 'border-gray-300 focus:ring-2 focus:ring-blue-500'
            ]" />
          <FieldError :message="errors.vmware_username" />
        </div>
        <div class="flex flex-col">
          <label for="vmware_password" class="mb-1 font-medium text-gray-700 dark:text-gray-50">Password</label>
          <input id="vmware_password" v-model="modelValue.vmware_password" type="password" autocomplete="new-password"
            @focus="$emit('clear-error', 'vmware_password')"
            :placeholder="modelValue.vmware_password_set ? '•••••• stored — leave blank to keep current' : 'Password'"
            :class="[
              'border rounded px-3 py-2 focus:outline-none dark:bg-gray-600 dark:text-gray-50',
              errors.vmware_password
                ? 'border-red-500 ring-2 ring-red-500 focus:ring-red-500'
                : 'border-gray-300 focus:ring-2 focus:ring-blue-500'
            ]" />
          <FieldError :message="errors.vmware_password" />
        </div>
        <div class="flex flex-col">
          <label for="vmware_collection_interval" class="mb-1 font-medium text-gray-700 dark:text-gray-50">Collection Interval</label>
          <input id="vmware_collection_interval" v-model="modelValue.vmware_collection_interval" placeholder="60s"
            class="border border-gray-300 dark:bg-gray-600 dark:text-gray-50 rounded px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500" />
        </div>
        <div class="flex flex-col">
          <label for="vmware_tls_insecure" class="mb-1 font-medium text-gray-700 dark:text-gray-50">TLS Skip Verify</label>
          <div class="relative inline-block w-12 align-middle select-none">
            <input v-model="modelValue.vmware_tls_insecure" id="vmware_tls_insecure" type="checkbox"
              class="absolute block w-6 h-6 rounded-full bg-white border-4 appearance-none cursor-pointer peer checked:right-0 checked:bg-blue-600 transition-all duration-300" />
            <label for="vmware_tls_insecure" class="block overflow-hidden h-6 rounded-full bg-gray-300 peer-checked:bg-blue-500 cursor-pointer"></label>
          </div>
        </div>
        <div class="flex flex-col">
          <label for="vmware_vsan_enabled" class="mb-1 font-medium text-gray-700 dark:text-gray-50">vSAN Metrics</label>
          <div class="relative inline-block w-12 align-middle select-none">
            <input v-model="modelValue.vmware_vsan_enabled" id="vmware_vsan_enabled" type="checkbox"
              class="absolute block w-6 h-6 rounded-full bg-white border-4 appearance-none cursor-pointer peer checked:right-0 checked:bg-blue-600 transition-all duration-300" />
            <label for="vmware_vsan_enabled" class="block overflow-hidden h-6 rounded-full bg-gray-300 peer-checked:bg-blue-500 cursor-pointer"></label>
          </div>
        </div>
        <div class="flex flex-col">
          <label for="vmware_per_vm_detail_enabled" class="mb-1 font-medium text-gray-700 dark:text-gray-50">Per-VM Detail Metrics</label>
          <div class="relative inline-block w-12 align-middle select-none">
            <input v-model="modelValue.vmware_per_vm_detail_enabled" id="vmware_per_vm_detail_enabled" type="checkbox"
              class="absolute block w-6 h-6 rounded-full bg-white border-4 appearance-none cursor-pointer peer checked:right-0 checked:bg-blue-600 transition-all duration-300" />
            <label for="vmware_per_vm_detail_enabled" class="block overflow-hidden h-6 rounded-full bg-gray-300 peer-checked:bg-blue-500 cursor-pointer"></label>
          </div>
        </div>
        <div class="flex flex-col">
          <label for="vmware_extended_vm_perf" class="mb-1 font-medium text-gray-700 dark:text-gray-50">Extended VM Perf Metrics</label>
          <div class="relative inline-block w-12 align-middle select-none">
            <input v-model="modelValue.vmware_extended_vm_perf" id="vmware_extended_vm_perf" type="checkbox"
              class="absolute block w-6 h-6 rounded-full bg-white border-4 appearance-none cursor-pointer peer checked:right-0 checked:bg-blue-600 transition-all duration-300" />
            <label for="vmware_extended_vm_perf" class="block overflow-hidden h-6 rounded-full bg-gray-300 peer-checked:bg-blue-500 cursor-pointer"></label>
          </div>
        </div>
      </div>
    </div>
    </div>
  </div>
</template>

<script setup>
import { computed } from 'vue'
import GlassSelect from '@/components/GlassSelect.vue'
import FieldError from '@/components/FieldError.vue'
import { useStore } from '@/store'

const props = defineProps({
  modelValue: {
    type: Object,
    required: true
  },
  osType: {
    type: String,
    default: ''
  },
  errors: {
    type: Object,
    default: () => ({})
  }
})

defineEmits(['update:modelValue', 'clear-error', 'start-tutorial'])

const isWindows = computed(() => props.osType === 'windows')
const isLinux = computed(() => props.osType === 'linux')
const isStaff = computed(() => useStore.isStaff())
// VMware (vCenter / ESXi) section hidden for now — flip to re-enable
const showVmware = false

const routesUnmatchedOptions = [
  { value: 'path', label: 'path' },
  { value: 'wildcard', label: 'wildcard' },
  { value: 'heuristic', label: 'heuristic' },
]

const windowsCollectorModules = [
  { field: 'collector_dns_enabled', label: 'DNS Server', description: 'Metrics only, requires the DNS Server role on the host.' },
  { field: 'collector_dfsr_enabled', label: 'DFS Replication (DFSR)', description: 'Metrics only, requires the DFS Replication role on the host.' },
]

const linuxCollectorModules = [
  
]

const instrumentationOptions = ['*', 'http', 'sql', 'grpc', 'redis', 'kafka', 'mongo', 'memcached', 'gpu']


const instrumentationList = computed(() => {
  try {
    const parsed = JSON.parse(props.modelValue.ebpf_instrumentations || '[]')
    return Array.isArray(parsed) ? parsed : []
  } catch {
    return []
  }
})

function setInstrumentations(items) {
  props.modelValue.ebpf_instrumentations = JSON.stringify(items)
}

function isInstrumentationSelected(option) {
  return instrumentationList.value.includes(option)
}

function toggleInstrumentation(option) {
  if (isInstrumentationSelected(option)) {
    setInstrumentations(instrumentationList.value.filter((item) => item !== option))
  } else {
    setInstrumentations([...instrumentationList.value, option])
  }
}
</script>
